import { createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react';
import { LocalAccount } from '../types/LocalAccount';
import { LocalAccountsOrder, useGetLocalAccounts } from '../api/queries/useGetLocalAccounts';
import { LocalAccountsFilters } from '../types/LocalAccountsFilters';
import { useGetUserData, UserSession } from '../../../api';
import { SystemData } from '../types/SystemData';
import { useGetSystemData } from '../api';
import { FilterMultipleValuesCount } from '../types/filters';
import {
    BINDING_STATUS_FILTER_DETAILS,
    MODE_FILTER_DETAILS
} from '../components/LocalAccounts/components/LocalAccountsToolbar/consts';
import { filterAllValuesFilters } from '../utils';

type LocalAccountsContextType = {
    setOrder: Dispatch<SetStateAction<LocalAccountsOrder>>;
    localAccounts: LocalAccount[];
    hasMore?: boolean;
    totalLocalAccountsCount?: number;
    fetchMore: () => void;
    mutate: () => Promise<void>;
    loading: boolean;
    error: unknown;
    filters: LocalAccountsFilters;
    setFilters: Dispatch<SetStateAction<LocalAccountsFilters>>;
    userSession?: UserSession;
    loadingUserSession: boolean;
    systemData?: SystemData;
};

const LocalAccountsContext = createContext<LocalAccountsContextType | undefined>(undefined);

const MULTIPLE_FILTERS: FilterMultipleValuesCount<LocalAccountsFilters> = {
    [BINDING_STATUS_FILTER_DETAILS.id]: BINDING_STATUS_FILTER_DETAILS.options.length,
    [MODE_FILTER_DETAILS.id]: MODE_FILTER_DETAILS.options.length
};

interface Props {
    children: ReactNode;
}

export const LocalAccountsProvider: FC<Props> = ({ children }) => {
    const [filters, setFilters] = useState<LocalAccountsFilters>({});
    const [apiFilters, setApiFilters] = useState<LocalAccountsFilters>({});

    const [order, setOrder] = useState<LocalAccountsOrder>({ orderBy: 'userName', orderDirection: 'asc' });
    
    const { userSession, loading: loadingUserSession } = useGetUserData();
    const { systemData, loading: loadingSystemData } = useGetSystemData();
    const {
        localAccounts = [],
        total: totalLocalAccountsCount,
        fetchData,
        loading: loadingLocalAccounts,
        ...restOfReturnValues
    } = useGetLocalAccounts({ filters: apiFilters, order });

    useEffect(() => {
        setApiFilters(filterAllValuesFilters(filters, MULTIPLE_FILTERS));
    }, [filters]);

    const contextValues = {
        ...restOfReturnValues,
        filters,
        setFilters,
        setOrder,
        mutate: fetchData,
        localAccounts,
        loadingLocalAccounts,
        loading: loadingLocalAccounts || loadingSystemData,
        totalLocalAccountsCount,
        userSession,
        loadingUserSession,
        systemData
    };

    return <LocalAccountsContext.Provider value={contextValues}>{children}</LocalAccountsContext.Provider>;
};

export const useLocalAccounts = (): LocalAccountsContextType => {
    const context = useContext(LocalAccountsContext);

    if (!context) {
        throw new Error('useLocalAccounts must be used within an LocalAccountsProvider');
    }

    return context;
};
